import { ref } from 'vue';
import { apiClient } from '@/Clients/ApiClient';
import PledgeService from '@/Services/PledgeService';
import ProjectsAdminService from '@/Services/Admin/ProjectsAdminService';
import OrdersService from '@/Services/OrdersService';
import PaymentsService from '@/Services/PaymentsService';
import ReCaptchaProvider from '@/Services/ReCaptcha/ReCaptchaProvider';
import { paymentDataSources } from '@/Models';
import { useOrderStore } from '@/Stores/Web/orderStore';
import {
    reCaptchaActions,
    defaultPaymentMethod,
} from '@/Components/Payments/Adyen/AdyenPaymentUtility';

export function useAdyenPaymentSubmission({ projectId, isInTestMode }) {
    const orderStore = useOrderStore();
    const pledgeService = new PledgeService(apiClient);
    const projectsAdminService = new ProjectsAdminService(apiClient);
    const ordersService = new OrdersService(apiClient);
    const paymentsService = new PaymentsService(apiClient);
    const reCaptchaProvider = new ReCaptchaProvider();
    const isCaptchaEnabled = ref(false);
    const storeCreditCard = ref(true);
    let reCaptchaToken = null;

    async function initCaptcha() {
        isCaptchaEnabled.value = await reCaptchaProvider.initAsync();
        return isCaptchaEnabled.value;
    }

    async function generateRecaptcha(action) {
        reCaptchaToken = await reCaptchaProvider.getTokenAsync(action);
    }

    async function executeWithCaptcha(action, serviceFn) {
        if (isCaptchaEnabled.value) {
            await generateRecaptcha(action);
        }
        return serviceFn();
    }

    // Payment method builders

    function buildCardPayment(paymentFormData) {
        return {
            ...defaultPaymentMethod,
            ...paymentFormData,
            paymentDataSource: paymentDataSources.newCard,
        };
    }

    function buildAdditionalPayment(paymentDataSource, paymentFormData) {
        return {
            ...defaultPaymentMethod,
            ...paymentFormData,
            paymentDataSource,
        };
    }

    function buildTokenPayment(paymentToken, email, paymentDataSource) {
        return {
            ...defaultPaymentMethod,
            paymentToken,
            email,
            paymentDataSource,
        };
    }

    function buildSystemPayment(paymentDataSource) {
        return {
            ...defaultPaymentMethod,
            paymentDataSource,
        };
    }

    // Submission

    function buildPaymentData(model, paymentMethod, { paymentAmountDetails, effectivePayableAmount, installmentsCount }) {
        return {
            ...model,
            // Both from one snapshot - reading the currency live lets the pair drift apart.
            dynamicCurrencyID: paymentAmountDetails?.dynamicCurrencyID ?? null,
            exchangeRate: paymentAmountDetails?.exchangeRate ?? null,
            paymentAmount: paymentAmountDetails?.paymentAmount,
            effectivePayableAmount,
            appliedGiftCardIDs: orderStore.appliedGiftCardIDs,
            paymentMethod,
            numberOfInstallments: installmentsCount,
        };
    }

    function resolveStrategy({ isBackupPayment, isInstantPayment, orderCode }) {
        if (isBackupPayment) return 'backup';
        if (isInstantPayment) return 'instant';
        if (orderCode) return 'deferredChange';
        return 'deferredCreate';
    }

    const strategies = {
        backup(paymentData) {
            return projectsAdminService.saveProjectBackupPaymentMethod(paymentData, projectId.value);
        },
        instant(paymentData, { orderCode, checkoutMode }) {
            return executeWithCaptcha(
                reCaptchaActions.createInstantPayment,
                () => pledgeService.createInstantPayment(
                    paymentData,
                    projectId.value,
                    orderCode,
                    checkoutMode,
                    reCaptchaToken,
                    reCaptchaActions.createInstantPayment,
                    isInTestMode.value,
                ),
            );
        },
        deferredCreate(paymentData) {
            return executeWithCaptcha(
                reCaptchaActions.createDeferredPayment,
                () => pledgeService.createDeferredPayment(
                    paymentData,
                    projectId.value,
                    reCaptchaToken,
                    reCaptchaActions.createDeferredPayment,
                    isInTestMode.value,
                ),
            );
        },
        deferredChange(paymentData, { orderCode }) {
            return executeWithCaptcha(
                reCaptchaActions.changeDeferredPayment,
                () => pledgeService.changeDeferredPayment(
                    paymentData,
                    projectId.value,
                    orderCode,
                    reCaptchaToken,
                    reCaptchaActions.changeDeferredPayment,
                    isInTestMode.value,
                ),
            );
        },
    };

    function submitPayment(paymentMethod, context) {
        const { model, paymentAmountDetails, effectivePayableAmount,
            installmentsCount, isInstantPayment,
            isBackupPayment, orderCode, checkoutMode } = context;

        const paymentData = buildPaymentData(model, paymentMethod, {
            paymentAmountDetails, effectivePayableAmount, installmentsCount,
        });
        const strategy = resolveStrategy({ isBackupPayment, isInstantPayment, orderCode });
        return strategies[strategy](paymentData, { orderCode, checkoutMode });
    }

    // Service wrappers

    function grantOrderAccess(email, createAccount) {
        return executeWithCaptcha(
            reCaptchaActions.grantOrderAccess,
            () => pledgeService.grantOrderAccess(
                email,
                projectId.value,
                createAccount,
                reCaptchaToken,
                reCaptchaActions.grantOrderAccess,
            ),
        );
    }

    function abortPayment(orderCode) {
        return ordersService.abortPayment(orderCode, projectId.value);
    }

    function abortCartPayment(orderCode) {
        return ordersService.abortCartPayment(orderCode, projectId.value);
    }

    async function validateApplePayMerchant() {
        const response = await paymentsService.createApplePaySession();
        return JSON.parse(response.data.data);
    }

    return {
        isCaptchaEnabled,
        storeCreditCard,

        initCaptcha,
        submitPayment,
        grantOrderAccess,
        abortPayment,
        abortCartPayment,
        validateApplePayMerchant,

        buildCardPayment,
        buildAdditionalPayment,
        buildTokenPayment,
        buildSystemPayment,
    };
}
